import { DateTime } from '@ksyos/date-time';
import { OrderedMap, Record } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import type { IJournal } from '@common/validation/episodesAndJournal';
import type { IEpisode } from '@frontend/components/interfaces';
import type { IJournalEntry } from '@frontend/components/interfaces/journalEntry';
import { fetchJson } from '@frontend/store/fetch';
import { ProfessionalSummaryDataAction } from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';

const episodeRecordType = Record({
    action: ProfessionalSummaryDataAction.add,
    codeCode: null,
    codeSystem: null,
    codeSystemName: null,
    comment: null,
    endDate: null,
    id: null,
    startDate: null,
    statusCode: null,
    valueDisplayName: null,
});

export class EpisodeRecord extends episodeRecordType {
    public readonly id: string;
    public readonly codeCode: string | null;
    public readonly codeSystem: string | null;
    public readonly codeSystemName: string | null;
    public readonly valueDisplayName: string | null;
    public readonly statusCode: string | null;
    public readonly comment: string | null;
    public readonly startDate: string | null;
    public readonly endDate: string | null;
    public readonly action: ProfessionalSummaryDataAction;

    constructor(values?: any) {
        super(values);
    }
}

export type Episodes = OrderedMap<string, EpisodeRecord>;

export type JournalEntries = OrderedMap<string, IJournal>;

export async function loadRequestEpisodes(
    patient: { authorization?: { canSeePatientEpisodes?: boolean }; id: string },
    requestId?: string
) {
    let episodes: Episodes = await loadPatientEpisode(patient);

    if (requestId) {
        const requestEpisodesResponse = await fetchJson(`/api/requests/${requestId}/requestEpisodes`, 'GET');
        for (const requestEpisode of requestEpisodesResponse.json.items) {
            const key = makeEpisodeKey(requestEpisode);
            if (episodes.has(key)) {
                episodes = episodes.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                episodes = episodes.set(uuidv4(), new EpisodeRecord(requestEpisode));
            }
        }
    }

    return episodes;
}

export async function loadProcessActivityEpisodes(
    patient: { authorization?: { canSeePatientEpisodes?: boolean }; id: string },
    processActivityId: string | null
) {
    let episodes: Episodes = await loadPatientEpisode(patient);

    if (processActivityId) {
        const processActivityEpisodesResponse = await fetchJson(
            `/api/processActivities/${processActivityId}/episodes`,
            'GET'
        );
        for (const processActivityEpisode of processActivityEpisodesResponse.json.items) {
            const key = makeEpisodeKey(processActivityEpisode);
            if (episodes.has(key)) {
                episodes = episodes.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
            } else {
                episodes = episodes.set(uuidv4(), new EpisodeRecord(processActivityEpisode));
            }
        }
    }

    return episodes;
}

async function loadPatientEpisode(
    patient: { authorization?: { canSeePatientEpisodes?: boolean }; id: string },
    _isEditEpisodes?: boolean
) {
    let episodes: Episodes = OrderedMap();
    if (patient.authorization && patient.authorization.canSeePatientEpisodes) {
        const patientEpisodesResponse = await fetchJson(`/api/patients/${patient.id}/episodes`, 'GET');
        for (const patientEpisode of patientEpisodesResponse.json.items) {
            episodes = episodes.set(
                makeEpisodeKey(patientEpisode.episode),
                new EpisodeRecord({ ...patientEpisode.episode, action: ProfessionalSummaryDataAction.skip })
            );
        }
    }

    return episodes;
}

function makeEpisodeKey(episode: IEpisode) {
    return (
        episode.codeCode +
        '\u0000' +
        episode.codeSystem +
        '\u0000' +
        episode.codeSystemName +
        '\u0000' +
        episode.valueDisplayName +
        '\u0000' +
        episode.comment +
        '\u0000' +
        episode.statusCode +
        '\u0000' +
        episode.startDate +
        '\u0000' +
        episode.endDate
    );
}

export function filterAndPrepareEpisodesForSaving(episodes?: Episodes) {
    if (!episodes) {
        return [];
    }

    return episodes
        .valueSeq()
        .filter((episode) => {
            if (!episode) {
                return false;
            } else {
                return episode.action === ProfessionalSummaryDataAction.pick;
            }
        })
        .toJS();
}

export function prepareAndSetJournalEntries(journals: IJournalEntry[], initialEpisodes: Episodes) {
    let results: JournalEntries = OrderedMap();
    for (const journalEntry of journals) {
        const journalEpisode = initialEpisodes.find(
            (episode) => episode?.valueDisplayName === journalEntry.episode.valueDisplayName
        );

        const journalEpisodeKey = journalEpisode.id;

        if (journalEpisode) {
            results = results.set(journalEpisodeKey, {
                ...results.get(journalEpisodeKey),
                episodeValueDisplayName: journalEntry.episode.valueDisplayName,
                [journalEntry.entryDisplayName]: journalEntry.comment,
            });
        }
    }

    return results;
}

export function prepareEpisodesForSaving(journalEntries?: JournalEntries) {
    if (!journalEntries) {
        return [];
    }

    return journalEntries
        .valueSeq()
        .filter((journalEntry) => {
            if (!journalEntry) {
                return false;
            } else {
                return !!journalEntry.episodeValueDisplayName;
            }
        })
        .toJS();
}

export function isCurrentEpisode(episode: { endDate: string | null; startDate: string | null }) {
    if (episode.startDate && DateTime.now().isBefore(episode.startDate)) {
        return false;
    }

    if (episode.endDate && DateTime.now().isAfter(episode.endDate)) {
        return false;
    }

    return true;
}
